import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2, Scale } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logCampaignActivity } from "@/lib/campaignHelpers";
import {
  useAccountCapacity,
  formatCapacityError,
  isCapacityError,
  type CapacityCampaign,
} from "@/hooks/useAccountCapacity";
import { cn } from "@/lib/utils";

interface CapacityRebalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Campaign to highlight (the one the user is trying to create/edit/resume). */
  focusCampaignId?: string | null;
  /** Optional pending values for a campaign not yet saved. */
  pendingCampaign?: { name: string; connections: number; messages: number } | null;
  onRebalanced?: () => void;
}

type Draft = Record<string, { connections: number; messages: number }>;

export function CapacityRebalanceDialog({
  open,
  onOpenChange,
  clientId,
  focusCampaignId,
  pendingCampaign,
  onRebalanced,
}: CapacityRebalanceDialogProps) {
  const { capacity, isLoading, invalidate } = useAccountCapacity(clientId, null, { enabled: open });
  const [draft, setDraft] = useState<Draft>({});
  const [isSaving, setIsSaving] = useState(false);

  const campaigns: CapacityCampaign[] = useMemo(() => capacity?.campaigns ?? [], [capacity]);

  useEffect(() => {
    if (!open || !capacity) return;
    const next: Draft = {};
    for (const c of capacity.campaigns) {
      next[c.id] = { connections: c.connections, messages: c.messages };
    }
    setDraft(next);
  }, [open, capacity]);

  const capConn = capacity?.capConnections ?? 20;
  const capMsg = capacity?.capMessages ?? 20;
  const pendingConn = pendingCampaign?.connections ?? 0;
  const pendingMsg = pendingCampaign?.messages ?? 0;

  const totalConn =
    Object.values(draft).reduce((s, v) => s + (v.connections || 0), 0) + pendingConn;
  const totalMsg =
    Object.values(draft).reduce((s, v) => s + (v.messages || 0), 0) + pendingMsg;

  const overConn = totalConn > capConn;
  const overMsg = totalMsg > capMsg;
  const canConfirm = !overConn && !overMsg && !isSaving && campaigns.length > 0;

  const setValue = (id: string, field: "connections" | "messages", raw: string) => {
    const parsed = parseInt(raw, 10);
    // Minimum 1: a campaign left at 0/day would silently never send anything.
    const value = Math.max(1, Math.min(capConn, Number.isNaN(parsed) ? 1 : parsed));
    setDraft((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));
  };

  const distributeEvenly = () => {
    if (!capacity) return;
    if (campaigns.length === 0) return;
    // The pending campaign keeps its requested allocation; the rest of the cap
    // is split between the existing campaigns.
    const remainingConn = Math.max(0, capConn - pendingConn);
    const remainingMsg = Math.max(0, capMsg - pendingMsg);
    const perConn = Math.max(1, Math.floor(remainingConn / campaigns.length));
    const perMsg = Math.max(1, Math.floor(remainingMsg / campaigns.length));
    const next: Draft = {};
    for (const c of campaigns) next[c.id] = { connections: perConn, messages: perMsg };
    setDraft(next);
  };


  const handleSave = async () => {
    if (!capacity) return;
    setIsSaving(true);
    try {
      const changed = campaigns.filter(
        (c) =>
          draft[c.id] &&
          (draft[c.id].connections !== c.connections || draft[c.id].messages !== c.messages)
      );

      // Pure reductions first (neither field increases), then everything else,
      // so the DB ceiling is never transiently exceeded on EITHER dimension.
      const isPureReduction = (c: CapacityCampaign) =>
        draft[c.id].connections <= c.connections && draft[c.id].messages <= c.messages;
      const ordered = [
        ...changed.filter(isPureReduction),
        ...changed.filter((c) => !isPureReduction(c)),
      ];

      const applied: string[] = [];

      try {
        for (const c of ordered) {
          const { error } = await supabase
            .from("campaigns")
            .update({
              max_connections_daily: draft[c.id].connections,
              max_messages_daily: draft[c.id].messages,
            })
            .eq("id", c.id);
          if (error) throw error;
          applied.push(c.name);

          if (draft[c.id].connections !== c.connections) {
            await logCampaignActivity(c.id, "connections_limit_changed", {
              from: c.connections,
              to: draft[c.id].connections,
              reason: "capacity_rebalance",
            });
          }
          if (draft[c.id].messages !== c.messages) {
            await logCampaignActivity(c.id, "messages_limit_changed", {
              from: c.messages,
              to: draft[c.id].messages,
              reason: "capacity_rebalance",
            });
          }
        }
      } catch (error: any) {
        // Partial application: refresh so the dialog shows the real state and
        // tell the user exactly what did go through.
        await invalidate();
        toast.error(
          `${isCapacityError(error) ? formatCapacityError(error) : error.message}${
            applied.length ? ` Applied so far: ${applied.join(", ")}.` : ""
          }`
        );
        setIsSaving(false);
        return;
      }

      await invalidate();
      toast.success(
        changed.length ? `Updated ${changed.length} campaign${changed.length > 1 ? "s" : ""}` : "No changes to apply"
      );
      onRebalanced?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(isCapacityError(error) ? formatCapacityError(error) : error.message);
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-4 h-4" />
            Rebalance daily capacity
          </DialogTitle>
          <DialogDescription>
            {capacity?.accountName} can perform at most {capConn} connections and {capMsg} messages
            per day in total. Split that across its campaigns below.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Connections</span>
                  <span className={cn("font-semibold", overConn ? "text-destructive" : "text-foreground")}>
                    {totalConn} / {capConn}
                  </span>
                </div>
                <div className="h-1.5 mt-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn("h-full", overConn ? "bg-destructive" : "bg-emerald-500")}
                    style={{ width: `${Math.min(100, (totalConn / Math.max(1, capConn)) * 100)}%` }}
                  />
                </div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Messages</span>
                  <span className={cn("font-semibold", overMsg ? "text-destructive" : "text-foreground")}>
                    {totalMsg} / {capMsg}
                  </span>
                </div>
                <div className="h-1.5 mt-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn("h-full", overMsg ? "bg-destructive" : "bg-emerald-500")}
                    style={{ width: `${Math.min(100, (totalMsg / Math.max(1, capMsg)) * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border divide-y max-h-[320px] overflow-y-auto">
              <div className="grid grid-cols-[1fr_88px_88px] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/40">
                <span>Campaign</span>
                <span className="text-center">Conn/day</span>
                <span className="text-center">Msg/day</span>
              </div>

              {pendingCampaign && (
                <div className="grid grid-cols-[1fr_88px_88px] gap-2 px-3 py-2.5 items-center bg-primary/5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{pendingCampaign.name}</p>
                    <Badge variant="secondary" className="mt-0.5 text-[10px]">New campaign</Badge>
                  </div>
                  <div className="text-center text-sm font-medium">{pendingConn}</div>
                  <div className="text-center text-sm font-medium">{pendingMsg}</div>
                </div>
              )}

              {campaigns.map((c) => (
                <div
                  key={c.id}
                  className={cn(
                    "grid grid-cols-[1fr_88px_88px] gap-2 px-3 py-2.5 items-center",
                    c.id === focusCampaignId && "bg-primary/5"
                  )}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    <Badge variant="outline" className="mt-0.5 text-[10px] capitalize">
                      {c.status}
                    </Badge>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={capConn}
                    value={draft[c.id]?.connections ?? c.connections}
                    onChange={(e) => setValue(c.id, "connections", e.target.value)}
                    className="h-8 text-center"
                  />
                  <Input
                    type="number"
                    min={1}
                    max={capMsg}
                    value={draft[c.id]?.messages ?? c.messages}
                    onChange={(e) => setValue(c.id, "messages", e.target.value)}
                    className="h-8 text-center"
                  />
                </div>
              ))}

              {campaigns.length === 0 && (
                <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                  No other campaigns are using this account.
                </div>
              )}
            </div>

            {(overConn || overMsg) && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Total exceeds what this account can safely do per day. Reduce{" "}
                  {overConn && overMsg ? "connections and messages" : overConn ? "connections" : "messages"} to
                  continue.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={distributeEvenly} disabled={isSaving || isLoading}>
            Distribute evenly
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canConfirm}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CapacityRebalanceDialog;
